/**
 * LoginScreen — Phase 1 dev-only: register/login bằng email + password.
 *
 * UC-01 + UC-02. Sau khi auth thành công:
 *   - Nếu shop chưa name → /onboarding/intro.
 *   - Nếu shop đã name → / (ShopScreen).
 */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { extractErrorMessage } from "../lib/api";
import { track } from "../lib/analytics";
import { useShopStore } from "../stores/shopStore";

type Mode = "login" | "register";

export function LoginScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const register = useShopStore((s) => s.register);
  const login = useShopStore((s) => s.login);
  const isLoading = useShopStore((s) => s.isLoading);

  const [mode, setMode] = useState<Mode>("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === "register") {
        await register(email, password);
        track("app_opened", { mode: "register" });
        navigate("/onboarding/intro");
      } else {
        await login(email, password);
        track("app_opened", { mode: "login" });
        const shop = useShopStore.getState().shop;
        navigate(shop?.name ? "/" : "/onboarding/intro");
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <main
      className="min-h-full flex items-center justify-center p-6 bg-cover bg-center relative"
      style={{ backgroundImage: "url(/assets/scenes/scene_1.jpg)" }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cream/80 backdrop-blur-sm"
      />
      <form
        onSubmit={onSubmit}
        className="relative bg-white rounded-2xl p-8 shadow-soft-amber border border-amber50/30 w-full max-w-sm"
      >
        <div className="flex items-center gap-3 mb-6">
          <span className="text-3xl">☕</span>
          <div>
            <h1 className="text-xl font-bold text-coffee">{t("app.title")}</h1>
            <p className="text-xs text-coffee/60">
              {mode === "register" ? t("auth.register_title") : t("auth.login_title")}
            </p>
          </div>
        </div>

        <label className="block text-sm font-medium text-coffee/80 mb-1">
          {t("auth.email")}
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            placeholder={t("auth.email_placeholder")}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-coffee/20 focus:border-coffee/60 outline-none"
          />
        </label>

        <label className="block text-sm font-medium text-coffee/80 mt-3 mb-1">
          {t("auth.password")}
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            placeholder={t("auth.password_placeholder")}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-coffee/20 focus:border-coffee/60 outline-none"
          />
        </label>

        {error && (
          <div className="mt-3 text-sm text-stool" data-testid="login-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="mt-5 w-full py-2.5 rounded-lg bg-coffee text-white font-semibold hover:bg-coffee/90 disabled:opacity-60"
        >
          {isLoading
            ? t("auth.processing")
            : mode === "register"
              ? t("auth.register_button")
              : t("auth.login_button")}
        </button>

        <button
          type="button"
          className="mt-3 w-full text-sm text-coffee/60 underline"
          onClick={() => {
            setMode(mode === "register" ? "login" : "register");
            setError(null);
          }}
        >
          {mode === "register"
            ? t("auth.switch_to_login")
            : t("auth.switch_to_register")}
        </button>
      </form>
    </main>
  );
}
